import dayjs from "dayjs";

const CATEGORY_ICON_MAP = {
  destination: "/images/icons/Destination.svg",
  destinations: "/images/icons/Destination.svg",
  activities: "/images/icons/Activities.svg",
  trips: "/images/icons/Trips.svg",
  traveller: "/images/icons/Traveler.svg",
  "things-to-do": "/images/icons/TTD.svg",
  "monthly-updates": "/images/icons/Monthlyupdates.svg",
};

export const BLOG_MONTH_SECTION_ID = "whats-on-month-by-month";

export function isMonthlyUpdatesCategory(item = {}) {
  const slug = String(item.slug || "").toLowerCase();
  const title = String(item.title || "").toLowerCase();
  return slug.includes("monthly") || title.includes("monthly");
}

export function getCategoryIconBySlug(slug = "") {
  return CATEGORY_ICON_MAP[String(slug || "").toLowerCase()] || "/images/icons/Destination.svg";
}

export function slugifyCategoryTabId(name = "") {
  return String(name || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export function normalizeBlogCategoriesResponse(response) {
  const items = Array.isArray(response?.data) ? response.data : [];

  return items
    .map((category) => ({
      id: String(category.categoryId || ""),
      title: category.categoryName || "",
      slug: category.slug || "",
      iconSrc: getCategoryIconBySlug(category.slug),
    }))
    .filter((category) => category.slug);
}

export function isBlogCategorySlug(slug = "", categories = []) {
  const normalizedSlug = String(slug || "").toLowerCase();
  if (!normalizedSlug) return false;

  return categories.some(
    (category) => String(category.slug || "").toLowerCase() === normalizedSlug
  );
}

export function findBlogCategory(slug = "", categories = []) {
  const normalizedSlug = String(slug || "").toLowerCase();
  return (
    categories.find(
      (category) => String(category.slug || "").toLowerCase() === normalizedSlug
    ) || null
  );
}

export function normalizeCategoryBlogsResponse(response, categorySlug = "") {
  const payload = response?.data ?? response ?? {};
  const regions = Array.isArray(payload.categoriesTags) ? payload.categoriesTags : [];

  const normalizedRegions = regions
    .map((region) => ({
      id: String(region.tagId || region.slug || region.tagName || ""),
      tagName: region.tagName || "",
      slug: region.slug || slugifyCategoryTabId(region.tagName),
      blogs: (Array.isArray(region.blogs) ? region.blogs : []).map(mapBlogListItem),
    }))
    .filter((region) => region.tagName);

  const items = normalizedRegions.flatMap((region) => region.blogs);

  return {
    categorySlug,
    pageBannerImage: payload.pageBannerimage || payload.pageBannerImage || "",
    pageBannerAlt: payload.pageBanneralt || payload.pageBannerAlt || "",
    regions: normalizedRegions,
    items,
    thingsToDo: payload.thingsToDo ?? null,
    totalCount: items.length,
  };
}

function getCategoryIcon(category = "") {
  const key = String(category).toLowerCase().trim();

  if (key.includes("activ")) return "/images/icons/travelinsight2.svg";
  if (key.includes("tip") || key.includes("traveller")) {
    return "/images/icons/travelinsight3.svg";
  }
  if (key.includes("theme") || key.includes("monthly")) {
    return "/images/icons/travelinsight4.svg";
  }

  return "/images/icons/travelinsight1.svg";
}

function formatBlogViews(count) {
  const num = Number(count);
  if (!Number.isFinite(num)) return "0 Views";
  return `${num.toLocaleString("en-IN")} Views`;
}

export function mapBlogListItem(blog = {}) {
  const category =
    Array.isArray(blog.categories) && blog.categories[0]
      ? String(blog.categories[0]).toUpperCase()
      : "DESTINATION";

  return {
    id: String(blog.blogId || blog.id || ""),
    image: blog.thumbnail || blog.bannerImage || "",
    date: blog.lastPublishedDate
      ? dayjs(blog.lastPublishedDate).format("DD MMM YYYY")
      : "",
    category,
    categoryIcon: getCategoryIcon(category),
    title: blog.title || "",
    description: blog.shortDescription || "",
    views: formatBlogViews(blog.views),
    slug: blog.slug || "",
  };
}

export function normalizeBlogsListResponse(response) {
  const payload = response?.data ?? response ?? {};
  const items = Array.isArray(payload.data) ? payload.data : [];

  return {
    items: items.map(mapBlogListItem),
    totalCount: Number(payload.totalCount) || items.length,
    pageIndex: Number(payload.pageIndex) || 1,
    pageSize: Number(payload.pageSize) || 12,
    totalPages: Number(payload.totalPages) || 1,
  };
}

export function getRegionPath(country = "UAE", path = "/") {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  if (country === "IN" && !normalizedPath.startsWith("/in")) {
    return `/in${normalizedPath}`;
  }
  return normalizedPath;
}

export function buildBlogListUrl(
  country = "UAE",
  { searchText = "", page = 1 } = {}
) {
  const base = getRegionPath(country, "/blog");
  const params = new URLSearchParams();
  const trimmedSearch = String(searchText || "").trim();

  if (trimmedSearch.length >= 3) {
    params.set("searchText", trimmedSearch);
  }
  if (page > 1) {
    params.set("page", String(page));
  }

  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export function formatAuthorNameFromSlug(authorSlug = "") {
  return String(authorSlug)
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function buildAuthorBlogListUrl(
  country = "UAE",
  { authorSlug = "", page = 1 } = {}
) {
  const base = getRegionPath(country, `/blog/author/${authorSlug}`);
  if (page > 1) {
    return `${base}?page=${page}`;
  }
  return base;
}

export function normalizeAuthorBlogsResponse(response) {
  const root = response?.data ?? response ?? {};
  const authorSource = root.author ?? root.authorDetail ?? root;
  const normalized = normalizeBlogsListResponse(response);

  return {
    ...normalized,
    author: {
      name:
        authorSource.name ||
        authorSource.authorName ||
        root.authorName ||
        "",
      slug:
        authorSource.slug ||
        authorSource.authorSlug ||
        root.authorSlug ||
        "",
      bio:
        authorSource.bio ||
        authorSource.description ||
        authorSource.about ||
        root.authorBio ||
        root.authorDescription ||
        "",
      image:
        authorSource.image ||
        authorSource.profileImage ||
        authorSource.avatar ||
        authorSource.authorImage ||
        root.authorImage ||
        root.profileImage ||
        "",
    },
  };
}

export async function fetchAuthorBlogListPages(
  { authorSlug = "", pageSize = 12, page = 1 },
  fetchPage
) {
  const items = [];
  let totalCount = 0;
  let totalPages = 1;
  let author = null;
  const targetPage = Math.max(1, Number(page) || 1);

  for (let pageIndex = 1; pageIndex <= targetPage; pageIndex += 1) {
    const response = await fetchPage({ authorSlug, pageSize, pageIndex });
    const normalized = normalizeAuthorBlogsResponse(response);

    if (!author && (normalized.author?.name || normalized.author?.bio)) {
      author = normalized.author;
    }

    items.push(...normalized.items);
    totalCount = normalized.totalCount;
    totalPages = normalized.totalPages;

    if (pageIndex >= totalPages) {
      break;
    }
  }

  const fallbackName = formatAuthorNameFromSlug(authorSlug);

  return {
    items,
    totalCount,
    pageIndex: Math.min(targetPage, totalPages),
    totalPages,
    pageSize,
    authorSlug,
    author: {
      name: author?.name || fallbackName,
      slug: author?.slug || authorSlug,
      bio: author?.bio || "",
      image: author?.image || "",
    },
  };
}

const MONTH_ICON_BY_SLUG = {
  january: "/images/icons/Jan.svg",
  february: "/images/icons/Feb.svg",
  march: "/images/icons/Mar.svg",
  april: "/images/icons/Apr.svg",
  may: "/images/icons/May.svg",
  june: "/images/icons/Jun.svg",
  july: "/images/icons/Jul.svg",
  august: "/images/icons/Aug.svg",
  september: "/images/icons/Sep.svg",
  october: "/images/icons/Oct.svg",
  november: "/images/icons/Nov.svg",
  december: "/images/icons/Dec.svg",
};

export function normalizeThingsToDoByMonthList(response) {
  const payload = response?.data ?? response ?? {};
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray(payload?.data)
      ? payload.data
      : [];

  return list
    .map((item, index) => {
      const slug = String(item.slug || "")
        .trim()
        .toLowerCase();

      const month = item.tagName || item.monthName || item.month || "";

      const count = Number(
        item.countOfBlog ??
        item.countofblog ??
        item.blogCount ??
        item.totalCount ??
        item.noOfBlogs ??
        item.count ??
        0
      );

      const slugKey = slug || slugifyCategoryTabId(month);

      return {
        id: String(item.tagId ?? item.id ?? index + 1),
        month,
        slug: slugKey,
        count: Number.isFinite(count) ? count : 0,
        icon: MONTH_ICON_BY_SLUG[slugKey] || "/images/icons/Monthlyupdates.svg",
      };
    })
    .filter((item) => item.month && item.slug);
}

export function formatMonthTitleFromSlug(slug = "") {
  return String(slug)
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function buildBlogMonthListUrl(
  country = "UAE",
  { monthSlug = "", page = 1, searchText = "" } = {}
) {
  const base = getRegionPath(country, `/blog/month/${monthSlug}`);
  const params = new URLSearchParams();
  const trimmedSearch = String(searchText || "").trim();

  if (trimmedSearch.length >= 3) {
    params.set("searchText", trimmedSearch);
  }
  if (page > 1) {
    params.set("page", String(page));
  }

  const query = params.toString();
  return query ? `${base}?${query}` : base;
}

export async function fetchBlogListPages(
  { searchText = "", pageSize = 12, page = 1, regionTagSlug = [] },
  fetchPage
) {
  const items = [];
  let totalCount = 0;
  let totalPages = 1;
  const targetPage = Math.max(1, Number(page) || 1);

  for (let pageIndex = 1; pageIndex <= targetPage; pageIndex += 1) {
    const response = await fetchPage({
      searchText,
      pageSize,
      pageIndex,
      regionTagSlug,
    });
    const normalized = normalizeBlogsListResponse(response);

    items.push(...normalized.items);
    totalCount = normalized.totalCount;
    totalPages = normalized.totalPages;

    if (pageIndex >= totalPages) {
      break;
    }
  }

  return {
    items,
    totalCount,
    pageIndex: Math.min(targetPage, totalPages),
    totalPages,
    pageSize,
    searchText,
    regionTagSlug,
  };
}

export function normalizeRecentBlogsResponse(response, { excludeSlug = "" } = {}) {
  const items = Array.isArray(response?.data) ? response.data : [];

  return items
    .map(mapBlogListItem)
    .filter((item) => item.slug && item.slug !== excludeSlug);
}

export function normalizeBlogDetailResponse(response, slug = "") {
  const root = response?.data ?? response ?? [];
  const entry = Array.isArray(root) ? root[0] : root;
  const detail = entry?.blogDetail ?? entry ?? {};
  const author = entry?.author ?? {};

  const tags = String(detail.tags || "")
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);

  const faqs = (Array.isArray(entry?.blogFaqs) ? entry.blogFaqs : [])
    .map((faq, index) => ({
      id: String(faq.id || faq.faqId || faq.blogFaqId || index),
      question: faq.question || faq.faqQuestion || faq.title || "",
      answer: faq.answer || faq.faqAnswer || faq.description || "",
    }))
    .filter((faq) => faq.question);

  return {
    id: String(detail.blogId || detail.id || ""),
    slug: detail.slug || slug,
    title: detail.title || "",
    shortDescription: detail.shortDescription || "",
    htmlContent: detail.description || "",
    bannerImage: detail.bannerImage || detail.thumbnail || "",
    thumbnail: detail.thumbnail || "",
    bannerAltText: detail.bannerAltText || detail.title || "",
    views: formatBlogViews(detail.views),
    viewsCount: Number(detail.views) || 0,
    date: detail.lastPublishedDate
      ? dayjs(detail.lastPublishedDate).format("DD MMM YYYY")
      : "",
    readTime: detail.timeToRead ? `${detail.timeToRead} min read` : "",
    tags,
    author: {
      name: author.name || detail.authorName || "",
      slug: author.slug || detail.authorSlug || "",
    },
    faqs,
    totalComments: Number(entry?.totalComments) || 0,
  };
}
